import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import audiosumLogo from '../assets/image/audiosum.png';
// import footerVideo from '../assets/video/Dark_Close_Up.mp4';

function Footer() {
    const { language } = useLanguage();
    const navigate = useNavigate();

    const handleInternalLinkClick = (url, e) => {
        e.preventDefault();
        
        // Check if URL has #faq anchor
        if (url.includes('#faq')) {
            const basePath = url.split('#')[0];
            navigate(basePath);
            // Wait for navigation, then scroll to FAQ section
            setTimeout(() => {
                const faqSection = document.getElementById('faq-section') || 
                                   document.querySelector('.faq-section') ||
                                   document.querySelector('[class*="FAQ"]') ||
                                   document.querySelector('section[class*="faq"]');
                if (faqSection) {
                    faqSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                } else {
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                }
            }, 300);
        } else {
            // For all other internal links, navigate and scroll to top
            navigate(url);
            setTimeout(() => {
                window.scrollTo({ top: 0, behavior: 'smooth' });
            }, 100);
        }
    };

    useEffect(() => {
        // Load Trustpilot widget script (if not already loaded in index.html)
        if (!document.querySelector('script[src*="trustpilot"]')) {
            const script = document.createElement('script');
            script.src = 'https://widget.trustpilot.com/bootstrap/v5/tp.widget.bootstrap.min.js';
            script.async = true;
            document.body.appendChild(script);
        }

        // Initialize Trustpilot widgets after render
        const initWidgets = () => {
            if (window.Trustpilot) {
                const widgets = document.querySelectorAll('.trustpilot-widget');
                widgets.forEach(widget => {
                    window.Trustpilot.loadFromElement(widget, true);
                });
            }
        };
        
        // Try to init immediately
        initWidgets();
        
        // Also try after a delay (in case script is still loading)
        const timeout = setTimeout(initWidgets, 1000);

        return () => {
            clearTimeout(timeout);
        };
    }, [language]); // Re-run when language changes

    const footerLinks = {
        en: {
            sections: [
                {
                    title: "Product",
                    links: [
                        { name: "AI Audio Summarizer", url: "/en/ai-audio-summarizer" },
                        // { name: "Watch Demo", url: "/en/watch-demo" }, // Temporarily hidden
                        { name: "AI Summarization", url: "https://audiosum.app/en/ai-summarization" },
                        { name: "Podcast Transcription", url: "https://audiosum.app/en/podcast-transcription" },
                        { name: "Meeting Transcription", url: "https://audiosum.app/en/meeting-transcription" },
                        { name: "Lecture Extraction", url: "https://audiosum.app/en/lecture-extraction" },
                        { name: "AI Chat", url: "https://audiosum.app/chatbot" }
                    ]
                },
                {
                    title: "Account",
                    links: [
                        { name: "Sign Up", url: "https://audiosum.app/signup" },
                        { name: "Sign In", url: "https://audiosum.app/login" },
                        { name: "My Account", url: "https://audiosum.app/account" },
                        { name: "My Plan", url: "https://audiosum.app/my-plan" },
                        { name: "Upload Files", url: "https://audiosum.app/upload" }
                    ]
                },
                {
                    title: "Support",
                    links: [
                        { name: "Pricing", url: "/en/pricing" },
                        { name: "FAQ", url: "/en#faq" },
                        { name: "Feedback", url: "https://forms.gle/uTShnyfhQYXqduNf8" },
                        { name: "Contact", url: "/en/contact" }
                    ]
                },
                {
                    title: "Company",
                    links: [
                        { name: "About", url: "/en/about" },
                        { name: "Contact", url: "/en/contact" },
                        { name: "Privacy Policy", url: "/en/privacy-policy" },
                        { name: "Terms & Conditions", url: "/en/terms-of-service" }
                    ]
                }
            ]
        },
        ar: {
            sections: [
                {
                    title: "المنتج",
                    links: [
                        { name: "ملخص الصوت بالذكاء الاصطناعي", url: "/ar/ai-audio-summarizer" },
                        // { name: "شاهد العرض", url: "/ar/watch-demo" }, // Temporarily hidden
                        { name: "الملخص بالذكاء الاصطناعي", url: "https://audiosum.app/ar/ai-summarization" },
                        { name: "نسخ البودكاست", url: "https://audiosum.app/ar/podcast-transcription" },
                        { name: "نسخ الاجتماعات", url: "https://audiosum.app/ar/meeting-transcription" },
                        { name: "استخراج المحاضرات", url: "https://audiosum.app/ar/lecture-extraction" },
                        { name: "المحادثة الذكية", url: "https://audiosum.app/chatbot" }
                    ]
                },
                {
                    title: "الحساب",
                    links: [
                        { name: "التسجيل", url: "https://audiosum.app/signup" },
                        { name: "تسجيل الدخول", url: "https://audiosum.app/login" },
                        { name: "حسابي", url: "https://audiosum.app/account" },
                        { name: "خطتي", url: "https://audiosum.app/my-plan" },
                        { name: "رفع الملفات", url: "https://audiosum.app/upload" }
                    ]
                },
                {
                    title: "الدعم",
                    links: [
                        { name: "الأسعار", url: "/ar/pricing" },
                        { name: "الأسئلة الشائعة", url: "/ar#faq" },
                        { name: "الملاحظات", url: "https://forms.gle/uTShnyfhQYXqduNf8" },
                        { name: "اتصل بنا", url: "/ar/contact" }
                    ]
                },
                {
                    title: "الشركة",
                    links: [
                        { name: "عن الشركة", url: "/ar/about" },
                        { name: "اتصل بنا", url: "/ar/contact" },
                        { name: "سياسة الخصوصية", url: "/ar/privacy-policy" },
                        { name: "الشروط والأحكام", url: "/ar/terms-of-service" }
                    ]
                }
            ]
        }
    };

    const currentLinks = footerLinks[language];

    return (
        <>
            <footer className='modern-footer'>
                <div className='footer-container'>
                    {/* Footer Main Content */}
                    <div className='footer-main-grid'>
                        {/* Brand Column */}
                        <div className='footer-brand-column'>
                            <Link to={`/${language}`} className='footer-logo-link'>
                                <img src={audiosumLogo} alt="Audiosum" className='footer-brand-logo' />
                            </Link>
                            <p className='footer-brand-tagline'>
                                {language === 'ar' 
                                    ? 'جعل تحليل الصوت والفيديو بالذكاء الاصطناعي أكثر سهولة.'
                                    : 'Audiosum - AI Summary for YouTube, Podcast, Books, PDF, Audio, Video and note taking. Save valuable time and improve learning efficiency by 50x.'}
                            </p>
                            
                            {/* Trustpilot Widget */}
                            <div style={{ marginLeft: '-50px', marginTop: '16px' }}>
                                <div 
                                    className="trustpilot-widget" 
                                    data-locale={language === 'ar' ? 'ar-AE' : 'en-US'}
                                    data-template-id="56278e9abfbbba0bdcd568bc" 
                                    data-businessunit-id="68def9c30126c7842a2f349d" 
                                    data-style-height="52px" 
                                    data-style-width="280px" 
                                    data-token="a31724db-39ea-405c-a24f-431b2fa79c9e"
                                >
                                    <a href="https://www.trustpilot.com/review/audiosum.ai" target="_blank" rel="noopener noreferrer">
                                        Trustpilot
                                    </a>
                                </div>
                            </div>
                        </div>

                        {/* Links Columns */}
                        {currentLinks.sections.map((section, index) => (
                            <div key={index} className='footer-links-column'>
                                <h3 className='footer-section-title'>{section.title}</h3>
                                <ul className='footer-links-list'>
                                    {section.links.map((link, linkIndex) => (
                                        <li key={linkIndex}>
                                            {link.url.startsWith('/') ? (
                                                <Link 
                                                    to={link.url} 
                                                    onClick={(e) => handleInternalLinkClick(link.url, e)}
                                                >
                                                    {link.name}
                                                </Link>
                                            ) : (
                                                <a href={link.url} target="_blank" rel="noopener noreferrer">
                                                    {link.name}
                                                </a>
                                            )}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </div>

                    {/* Footer Bottom Bar */}
                    <div className='footer-bottom'>
                        <div className='footer-bottom-left'>
                            <span>©Audiosum 2025. All rights reserved.</span>
                        </div>
                    </div>
                </div>
            </footer>
        </>
    )
}

export default Footer
